import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { LOGO } from "@/lib/assets";
import {
  LayoutDashboard, CalendarDays, CalendarRange, Users, CreditCard, Palette, LogOut, Download,
  Plus, Trash2, Loader2, Check, X, Baby, Phone, AlertTriangle, Euro, Sun, Sunset, ChevronDown, Settings, Megaphone, CalendarClock, Palmtree, Sparkles, FileText, Upload,
} from "lucide-react";

const TABS = [
  { id: "dashboard", label: "Tableau de bord", icon: LayoutDashboard },
  { id: "calendar", label: "Calendrier", icon: CalendarRange },
  { id: "announcements", label: "Annonces", icon: Megaphone },
  { id: "pricing", label: "Tarifs", icon: Euro },
  { id: "ages", label: "Tranches d'âge", icon: Baby },
  { id: "specialevents", label: "Événements spéciaux", icon: Sparkles },
  { id: "upcoming", label: "À venir", icon: CalendarClock },
  { id: "periods", label: "Périodes vacances", icon: Palmtree },
  { id: "bookings", label: "Réservations", icon: CalendarDays },
  { id: "memberships", label: "Adhésions", icon: CreditCard },
  { id: "campdates", label: "Dates (camp & ateliers)", icon: CalendarDays },
  { id: "workshops", label: "Ateliers récurrents", icon: Palette },
  { id: "kidscamp", label: "Kids Camp", icon: Sun },
  { id: "parents", label: "Parents & enfants", icon: Users },
  { id: "documents", label: "Documents", icon: FileText },
  { id: "settings", label: "Paramètres du site", icon: Settings },
];

const STATUS_COLORS = {
  paid: "bg-[#5e7d52]/15 text-[#4a6742]",
  pending: "bg-[#e0b36a]/25 text-[#8a6a1f]",
  cancelled: "bg-[#e63946]/12 text-[#b5232f]",
  expired: "bg-[#6f7d54]/10 text-[#3a4d68]",
};

export default function AdminDashboard() {
  const { admin, logout, authAxios } = useAdminAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");

  useEffect(() => {
    if (admin === false) navigate("/admin/login");
  }, [admin, navigate]);

  if (admin === null) return <div className="min-h-screen bg-[#fbf6ec] flex items-center justify-center"><Loader2 className="animate-spin text-[#e07a5f]" size={40} /></div>;
  if (!admin) return null;

  const api = authAxios();

  return (
    <div className="min-h-screen bg-[#fbf6ec]" data-testid="admin-dashboard">
      {/* Topbar */}
      <header className="bg-[#6f7d54] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
          <img src={LOGO} alt="Island Camps" className="h-9" />
          <div className="flex items-center gap-3">
            <span className="hidden sm:block font-body text-[#f7efe0]/70 text-sm">{admin.email}</span>
            <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/20 text-white font-bold text-sm px-4 py-2 transition-colors">
              <LogOut size={16} /> Déconnexion
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 lg:px-8 py-6 grid lg:grid-cols-[220px_1fr] gap-6">
        {/* Tabs */}
        <nav className="flex lg:flex-col gap-2 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
          {TABS.map((tb) => {
            const Icon = tb.icon;
            return (
              <button key={tb.id} data-testid={`tab-${tb.id}`} onClick={() => setTab(tb.id)} className={`shrink-0 inline-flex items-center gap-2.5 rounded-2xl px-4 py-3 font-body font-semibold text-sm transition-all text-left ${tab === tb.id ? "bg-[#e07a5f] text-white shadow" : "bg-white text-[#3a4d68] hover:bg-white/70"}`}>
                <Icon size={18} /> {tb.label}
              </button>
            );
          })}
        </nav>

        <main className="min-w-0">
          {tab === "dashboard" && <DashboardTab api={api} />}
          {tab === "calendar" && <CalendarTab api={api} />}
          {tab === "announcements" && <AnnouncementsTab api={api} />}
          {tab === "pricing" && <PricingTab api={api} />}
          {tab === "ages" && <AgeGroupsTab api={api} />}
          {tab === "specialevents" && <SpecialEventsTab api={api} />}
          {tab === "upcoming" && <UpcomingEventTab api={api} />}
          {tab === "periods" && <VacationPeriodsTab api={api} />}
          {tab === "bookings" && <BookingsTab api={api} />}
          {tab === "memberships" && <MembershipsTab api={api} />}
          {tab === "campdates" && <CampDatesTab api={api} />}
          {tab === "workshops" && <WorkshopsTab api={api} />}
          {tab === "kidscamp" && <KidsCampTab api={api} />}
          {tab === "parents" && <ParentsTab api={api} />}
          {tab === "documents" && <DocumentsTab api={api} />}
          {tab === "settings" && <SiteSettingsTab api={api} />}
        </main>
      </div>
    </div>
  );
}

function SiteSettingsTab({ api }) {
  const [s, setS] = useState({});
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  useEffect(() => { api.get("/admin/site-settings").then((r) => { setS(r.data || {}); setLoading(false); }).catch(() => setLoading(false)); }, [api]);
  const f = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const save = async () => { await api.put("/admin/site-settings", s); setSaved(true); setTimeout(() => setSaved(false), 2500); };
  if (loading) return <Loader2 className="animate-spin text-[#e07a5f]" />;
  return (
    <div data-testid="settings-tab">
      <div className="flex items-center justify-between mb-4">
        <H2>Paramètres du site</H2>
        <button onClick={save} data-testid="settings-save" className="inline-flex items-center gap-1.5 rounded-full bg-[#5e7d52] text-white font-bold text-sm px-4 py-2"><Check size={15} /> {saved ? "Enregistré ✓" : "Enregistrer"}</button>
      </div>
      <Card className="p-5 mb-5 space-y-4">
        <p className="font-head font-bold text-[#6f7d54]">Contact & réseaux</p>
        <div className="grid md:grid-cols-2 gap-4">
          <KcField testid="set-email" label="E-mail" value={s.email} onChange={(v) => f("email", v)} />
          <KcField testid="set-phone" label="Téléphone (optionnel)" value={s.phone} onChange={(v) => f("phone", v)} />
          <KcField testid="set-insta-handle" label="Instagram (handle)" value={s.instagram_handle} onChange={(v) => f("instagram_handle", v)} />
          <KcField testid="set-insta-url" label="Instagram (lien)" value={s.instagram_url} onChange={(v) => f("instagram_url", v)} />
        </div>
      </Card>
      <Card className="p-5 mb-5 space-y-4">
        <p className="font-head font-bold text-[#6f7d54]">Slogan</p>
        <div className="grid md:grid-cols-2 gap-4">
          <KcField testid="set-slogan-fr" label="Slogan FR" area value={s.slogan_fr} onChange={(v) => f("slogan_fr", v)} />
          <KcField testid="set-slogan-en" label="Slogan EN" area value={s.slogan_en} onChange={(v) => f("slogan_en", v)} />
        </div>
      </Card>
      <Card className="p-5 mb-5 space-y-4">
        <p className="font-head font-bold text-[#6f7d54]">Mentions légales</p>
        <div className="grid md:grid-cols-2 gap-4">
          <KcField testid="set-addr1" label="Adresse (ligne 1)" value={s.address_line1} onChange={(v) => f("address_line1", v)} />
          <KcField testid="set-addr2" label="Adresse (ligne 2)" value={s.address_line2} onChange={(v) => f("address_line2", v)} />
          <KcField testid="set-reg" label="N° d'enregistrement" value={s.reg_number} onChange={(v) => f("reg_number", v)} />
          <KcField testid="set-created" label="Date de création" value={s.creation_date} onChange={(v) => f("creation_date", v)} />
          <KcField testid="set-authority" label="Autorité d'enregistrement" value={s.authority} onChange={(v) => f("authority", v)} />
          <KcField testid="set-acm" label="Code organisateur ACM" value={s.acm_code} onChange={(v) => f("acm_code", v)} />
        </div>
      </Card>
      <Card className="p-5 mb-5 space-y-4">
        <p className="font-head font-bold text-[#6f7d54]">Coordonnées bancaires (virement)</p>
        <p className="font-body text-sm text-[#3a4d68]/70">Affichées aux parents pour le paiement par virement. Laissez vide pour ne rien afficher.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <KcField testid="set-bank-name" label="Banque" value={s.bank_name} onChange={(v) => f("bank_name", v)} />
          <KcField testid="set-rib-holder" label="Titulaire du compte" value={s.rib_holder} onChange={(v) => f("rib_holder", v)} />
          <KcField testid="set-rib-iban" label="IBAN" value={s.rib_iban} onChange={(v) => f("rib_iban", v)} />
          <KcField testid="set-rib-bic" label="BIC / SWIFT" value={s.rib_bic} onChange={(v) => f("rib_bic", v)} />
        </div>
      </Card>
      <button onClick={save} data-testid="settings-save-bottom" className="inline-flex items-center gap-1.5 rounded-full bg-[#5e7d52] text-white font-bold text-sm px-5 py-2.5"><Check size={15} /> {saved ? "Enregistré ✓" : "Enregistrer les modifications"}</button>
    </div>
  );
}

function Card({ children, className = "" }) {
  return <div className={`bg-white rounded-2xl border border-[#6f7d54]/10 ${className}`}>{children}</div>;
}
function KcField({ label, value, onChange, area, testid }) {
  return (
    <label className="block">
      <span className="block font-semibold text-sm text-[#3a4d68] mb-1">{label}</span>
      {area
        ? <textarea data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" />
        : <input data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" />}
    </label>
  );
}
function KidsCampTab({ api }) {
  const empty = { hero_kicker_fr: "", hero_kicker_en: "", hero_title_fr: "", hero_title_en: "", hero_subtitle_fr: "", hero_subtitle_en: "", concept_title_fr: "", concept_title_en: "", concept_text_fr: "", concept_text_en: "", themes_title_fr: "", themes_title_en: "", weeks: [] };
  const [s, setS] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  useEffect(() => { api.get("/admin/kids-camp").then((r) => { setS({ ...empty, ...(r.data || {}), weeks: (r.data && r.data.weeks) || [] }); setLoading(false); }).catch(() => setLoading(false)); }, [api]);
  const f = (k, v) => setS((p) => ({ ...p, [k]: v }));
  const addWeek = () => setS((p) => ({ ...p, weeks: [...p.weeks, { emoji: "", week_fr: "", week_en: "", name_fr: "", name_en: "", desc_fr: "", desc_en: "" }] }));
  const upWeek = (i, k, v) => setS((p) => ({ ...p, weeks: p.weeks.map((w, j) => (j === i ? { ...w, [k]: v } : w)) }));
  const delWeek = (i) => setS((p) => ({ ...p, weeks: p.weeks.filter((_, j) => j !== i) }));
  const save = async () => { await api.put("/admin/kids-camp", s); setSaved(true); setTimeout(() => setSaved(false), 2500); };
  if (loading) return <Loader2 className="animate-spin text-[#e07a5f]" />;
  return (
    <div data-testid="kidscamp-tab">
      <div className="flex items-center justify-between mb-3">
        <H2>Kids Camp — contenu éditable</H2>
        <button onClick={save} data-testid="kc-save" className="inline-flex items-center gap-1.5 rounded-full bg-[#5e7d52] text-white font-bold text-sm px-4 py-2"><Check size={15} /> {saved ? "Enregistré ✓" : "Enregistrer"}</button>
      </div>
      <p className="font-body text-sm text-[#3a4d68] mb-4">Laissez un champ vide pour garder le texte par défaut du site. Remplissez pour personnaliser (ex. « Summer Kids Camp » → « Halloween Kids Camp »).</p>
      <Card className="p-5 mb-5 space-y-4">
        <p className="font-head font-bold text-[#6f7d54]">En-tête (Hero)</p>
        <div className="grid md:grid-cols-2 gap-4">
          <KcField testid="kc-hero_kicker_fr" label="Badge FR" value={s.hero_kicker_fr} onChange={(v) => f("hero_kicker_fr", v)} />
          <KcField testid="kc-hero_kicker_en" label="Badge EN" value={s.hero_kicker_en} onChange={(v) => f("hero_kicker_en", v)} />
          <KcField testid="kc-hero_title_fr" label="Titre FR" value={s.hero_title_fr} onChange={(v) => f("hero_title_fr", v)} />
          <KcField testid="kc-hero_title_en" label="Titre EN" value={s.hero_title_en} onChange={(v) => f("hero_title_en", v)} />
          <KcField testid="kc-hero_subtitle_fr" label="Sous-titre FR" area value={s.hero_subtitle_fr} onChange={(v) => f("hero_subtitle_fr", v)} />
          <KcField testid="kc-hero_subtitle_en" label="Sous-titre EN" area value={s.hero_subtitle_en} onChange={(v) => f("hero_subtitle_en", v)} />
        </div>
      </Card>
      <Card className="p-5 mb-5 space-y-4">
        <p className="font-head font-bold text-[#6f7d54]">Fil rouge (Concept)</p>
        <div className="grid md:grid-cols-2 gap-4">
          <KcField testid="kc-concept_title_fr" label="Titre FR" value={s.concept_title_fr} onChange={(v) => f("concept_title_fr", v)} />
          <KcField testid="kc-concept_title_en" label="Titre EN" value={s.concept_title_en} onChange={(v) => f("concept_title_en", v)} />
          <KcField testid="kc-concept_text_fr" label="Texte FR" area value={s.concept_text_fr} onChange={(v) => f("concept_text_fr", v)} />
          <KcField testid="kc-concept_text_en" label="Texte EN" area value={s.concept_text_en} onChange={(v) => f("concept_text_en", v)} />
        </div>
      </Card>
      <Card className="p-5 mb-5 space-y-4">
        <div className="flex items-center justify-between">
          <p className="font-head font-bold text-[#6f7d54]">Semaines & thèmes</p>
          <button onClick={addWeek} data-testid="kc-add-week" className="inline-flex items-center gap-1.5 rounded-full bg-[#e07a5f]/12 text-[#cf6a50] font-bold text-sm px-4 py-2"><Plus size={15} /> Ajouter une semaine</button>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <KcField testid="kc-themes_title_fr" label="Titre section FR" value={s.themes_title_fr} onChange={(v) => f("themes_title_fr", v)} />
          <KcField testid="kc-themes_title_en" label="Titre section EN" value={s.themes_title_en} onChange={(v) => f("themes_title_en", v)} />
        </div>
        <div className="space-y-4">
          {s.weeks.map((w, i) => (
            <div key={i} data-testid={`kc-week-${i}`} className="bg-[#fbf6ec] rounded-2xl border border-[#6f7d54]/10 p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="font-head font-bold text-[#6f7d54]">Semaine {i + 1}</span>
                <button onClick={() => delWeek(i)} data-testid={`kc-del-week-${i}`} className="text-[#e63946]"><Trash2 size={15} /></button>
              </div>
              <div className="grid md:grid-cols-2 gap-3">
                <KcField testid={`kc-week-emoji-${i}`} label="Emoji" value={w.emoji} onChange={(v) => upWeek(i, "emoji", v)} />
                <div />
                <KcField testid={`kc-week-labelfr-${i}`} label="Label FR (ex. Semaine 1)" value={w.week_fr} onChange={(v) => upWeek(i, "week_fr", v)} />
                <KcField testid={`kc-week-labelen-${i}`} label="Label EN" value={w.week_en} onChange={(v) => upWeek(i, "week_en", v)} />
                <KcField testid={`kc-week-namefr-${i}`} label="Thème FR" value={w.name_fr} onChange={(v) => upWeek(i, "name_fr", v)} />
                <KcField testid={`kc-week-nameen-${i}`} label="Thème EN" value={w.name_en} onChange={(v) => upWeek(i, "name_en", v)} />
                <KcField testid={`kc-week-descfr-${i}`} label="Description FR" area value={w.desc_fr} onChange={(v) => upWeek(i, "desc_fr", v)} />
                <KcField testid={`kc-week-descen-${i}`} label="Description EN" area value={w.desc_en} onChange={(v) => upWeek(i, "desc_en", v)} />
              </div>
            </div>
          ))}
        </div>
      </Card>
      <button onClick={save} data-testid="kc-save-bottom" className="inline-flex items-center gap-1.5 rounded-full bg-[#5e7d52] text-white font-bold text-sm px-5 py-2.5"><Check size={15} /> {saved ? "Enregistré ✓" : "Enregistrer les modifications"}</button>
    </div>
  );
}
function H2({ children }) {
  return <h2 className="font-head font-bold text-2xl text-[#6f7d54] mb-5">{children}</h2>;
}
function Badge({ status }) {
  return <span className={`rounded-full text-xs font-bold px-2.5 py-1 ${STATUS_COLORS[status] || STATUS_COLORS.expired}`}>{status}</span>;
}

async function downloadCsv(api, url, filename) {
  const res = await api.get(url, { responseType: "blob" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(res.data);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
}

/* ---------------- DASHBOARD ---------------- */
function DashboardTab({ api }) {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, [api]);
  const cards = stats ? [
    { label: "Réservations payées", value: stats.paid_bookings, icon: CalendarDays },
    { label: "En attente", value: stats.pending_bookings, icon: Loader2 },
    { label: "Adhérents", value: stats.paid_members, icon: CreditCard },
    { label: "Enfants inscrits", value: stats.children_enrolled, icon: Baby },
    { label: "Revenus (€)", value: stats.revenue, icon: Euro },
  ] : [];
  return (
    <div data-testid="dashboard-tab">
      <H2>Tableau de bord</H2>
      {!stats ? <Loader2 className="animate-spin text-[#e07a5f]" /> : (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <Card key={i} className="p-5">
                <Icon className="text-[#e07a5f] mb-2" size={22} />
                <div className="font-head font-extrabold text-3xl text-[#6f7d54]" data-testid={`stat-${i}`}>{c.value}</div>
                <div className="font-body text-[#3a4d68] text-sm mt-1">{c.label}</div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ---------------- CALENDAR OVERVIEW ---------------- */
const MONTHS_FR = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const DOW_FR = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

function SessionBlock({ date, period, label, Icon, data, expanded, toggle }) {
  if (!data.open) return (
    <div className="rounded-xl bg-[#6f7d54]/5 px-3 py-2 text-center text-xs font-semibold text-[#3a4d68]/50">{label} · fermé</div>
  );
  const pct = data.capacity ? Math.min(100, Math.round((data.enrolled_count / data.capacity) * 100)) : 0;
  const full = data.remaining <= 0;
  const key = `${date}-${period}`;
  return (
    <div className="rounded-xl border border-[#6f7d54]/10 overflow-hidden">
      <button onClick={() => toggle(key)} data-testid={`cal-session-${key}`} className="w-full px-3 py-2.5 text-left">
        <div className="flex items-center justify-between mb-1.5">
          <span className="flex items-center gap-1.5 font-bold text-sm text-[#6f7d54]"><Icon size={14} className="text-[#e07a5f]" /> {label}</span>
          <span className={`text-xs font-bold rounded-full px-2 py-0.5 ${full ? "bg-[#e63946]/12 text-[#b5232f]" : "bg-[#5e7d52]/15 text-[#4a6742]"}`}>{full ? "Complet" : `${data.remaining} places`}</span>
        </div>
        <div className="h-1.5 rounded-full bg-[#6f7d54]/8 overflow-hidden">
          <div className={`h-full ${full ? "bg-[#e63946]" : "bg-[#2e8b9e]"}`} style={{ width: `${pct}%` }} />
        </div>
        <div className="flex items-center justify-between mt-1.5">
          <span className="text-xs text-[#3a4d68]">{data.enrolled_count}/{data.capacity} inscrits{data.pending_count ? ` · ${data.pending_count} en attente` : ""}</span>
          {data.enrolled_count > 0 && <ChevronDown size={14} className={`text-[#3a4d68] transition-transform ${expanded ? "rotate-180" : ""}`} />}
        </div>
      </button>
      {expanded && data.enrolled.length > 0 && (
        <div className="border-t border-[#6f7d54]/10 bg-[#fbf6ec]/60 px-3 py-2 space-y-2">
          {data.enrolled.map((e, i) => (
            <div key={i} className="text-xs">
              <div className="font-bold text-[#6f7d54] flex items-center gap-1"><Baby size={12} /> {e.child} {e.age_group ? <span className="font-normal text-[#2e8b9e]">· {e.age_group}</span> : null} <span className="font-normal text-[#3a4d68]">· {e.parent}</span></div>
              {e.allergies ? <div className="text-[#cf6a50] flex items-center gap-1"><AlertTriangle size={11} /> {e.allergies}</div> : null}
              {(e.emergency_name || e.emergency_phone) ? <div className="text-[#3a4d68] flex items-center gap-1"><Phone size={11} /> {e.emergency_name} {e.emergency_phone}</div> : null}
              <div className="flex flex-wrap gap-x-2 gap-y-0.5 mt-0.5">
                <span className={e.swimming ? "text-[#5e7d52]" : "text-[#cf6a50]"}>Baignade {e.swimming ? "✓" : "✗"}</span>
                <span className={e.image_rights ? "text-[#5e7d52]" : "text-[#cf6a50]"}>Image {e.image_rights ? "✓" : "✗"}</span>
                {e.authorized_pickup ? <span className="text-[#3a4d68]">· Récup. : {e.authorized_pickup}</span> : null}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function CalendarTab({ api }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState({});
  useEffect(() => { api.get("/admin/calendar").then((r) => setData(r.data)).catch(() => setData({ dates: [], workshops: [] })); }, [api]);
  const toggle = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

  if (!data) return <Loader2 className="animate-spin text-[#e07a5f]" />;

  // group by month
  const months = {};
  for (const d of data.dates) {
    const [y, m] = d.date.split("-").map(Number);
    const key = `${y}-${m}`;
    (months[key] = months[key] || { y, m, days: [] }).days.push(d);
  }
  const monthList = Object.values(months).sort((a, b) => (a.y - b.y) || (a.m - b.m));

  const totalEnrolled = data.dates.reduce((s, d) => s + (d.morning.enrolled_count || 0) + (d.afternoon.enrolled_count || 0), 0);

  return (
    <div data-testid="calendar-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <H2>Calendrier des sessions</H2>
        <div className="flex items-center gap-2 text-sm font-body">
          <span className="rounded-full bg-[#2e8b9e]/12 text-[#2e8b9e] font-bold px-3 py-1.5">{totalEnrolled} inscriptions</span>
          <span className="rounded-full bg-[#5e7d52]/15 text-[#4a6742] font-bold px-3 py-1.5">{data.dates.length} journées</span>
        </div>
      </div>

      {monthList.length === 0 ? <p className="font-body text-[#3a4d68]">Aucune date de camp. Ajoutez-en dans l'onglet « Dates de camp ».</p> : (
        <div className="space-y-8">
          {monthList.map((mo) => (
            <div key={`${mo.y}-${mo.m}`}>
              <h3 className="font-head font-bold text-[#2e8b9e] text-lg mb-3">{MONTHS_FR[mo.m - 1]} {mo.y}</h3>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {mo.days.map((d) => {
                  const [yy, mm, dd] = d.date.split("-").map(Number);
                  const dow = new Date(yy, mm - 1, dd).getDay();
                  return (
                    <Card key={d.date} className={`p-3 ${d.closed ? "opacity-50" : ""}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-head font-bold text-[#6f7d54]">{DOW_FR[dow]} {dd}</span>
                        {d.closed && <span className="text-xs font-bold text-[#b5232f]">Fermé</span>}
                      </div>
                      <div className="space-y-2">
                        <SessionBlock date={d.date} period="morning" label="Matin" Icon={Sun} data={d.morning} expanded={expanded[`${d.date}-morning`]} toggle={toggle} />
                        <SessionBlock date={d.date} period="afternoon" label="Après-midi" Icon={Sunset} data={d.afternoon} expanded={expanded[`${d.date}-afternoon`]} toggle={toggle} />
                      </div>
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {data.workshops.length > 0 && (
        <div className="mt-10">
          <h3 className="font-head font-bold text-[#e07a5f] text-lg mb-3">Ateliers</h3>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.workshops.map((w) => (
              <Card key={w.id} className="p-4 flex items-center justify-between">
                <div>
                  <div className="font-head font-bold text-[#6f7d54]">{w.title}</div>
                  <div className="text-sm text-[#3a4d68]">{w.day} · {w.age} · {w.spots} places</div>
                </div>
                <span className={`text-xs font-bold rounded-full px-3 py-1 ${w.available ? "bg-[#5e7d52]/15 text-[#4a6742]" : "bg-[#6f7d54]/8 text-[#3a4d68]"}`}>{w.available ? "Actif" : "Bientôt"}</span>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- BOOKINGS ---------------- */
function BookingsTab({ api }) {
  const [bookings, setBookings] = useState([]);
  const [dateFilter, setDateFilter] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/bookings", { params: dateFilter ? { date: dateFilter } : {} }).then((r) => { setBookings(r.data.bookings); setLoading(false); }).catch(() => setLoading(false));
  }, [api, dateFilter]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => { await api.patch(`/admin/bookings/${id}/status`, { status }); load(); };
  const cancel = async (id) => { await api.post(`/admin/bookings/${id}/cancel`); load(); };
  const del = async (id) => { if (!window.confirm("Supprimer définitivement cette réservation ?")) return; await api.delete(`/admin/bookings/${id}`); load(); };
  const setPay = async (id, pm) => { await api.post(`/admin/bookings/${id}/payment-method`, { payment_method: pm }); load(); };
  const PAY_LABELS = { online: "Lien en ligne", virement: "Virement", especes: "Espèces" };

  return (
    <div data-testid="bookings-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <H2>Réservations</H2>
        <div className="flex items-center gap-2">
          <input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} data-testid="booking-date-filter" className="rounded-xl border border-[#6f7d54]/15 px-3 py-2 font-body text-sm" />
          {dateFilter && <button onClick={() => setDateFilter("")} className="text-sm text-[#cf6a50] font-bold">Réinitialiser</button>}
          <button onClick={() => downloadCsv(api, "/admin/export/bookings.csv", "reservations.csv")} data-testid="export-bookings" className="inline-flex items-center gap-1.5 rounded-full bg-[#6f7d54] text-white font-bold text-sm px-4 py-2"><Download size={15} /> CSV</button>
          {dateFilter && <button onClick={() => downloadCsv(api, `/admin/export/attendance.csv?date=${dateFilter}`, `presence_${dateFilter}.csv`)} data-testid="export-attendance" className="inline-flex items-center gap-1.5 rounded-full bg-[#2e8b9e] text-white font-bold text-sm px-4 py-2"><Download size={15} /> Présences</button>}
        </div>
      </div>
      {loading ? <Loader2 className="animate-spin text-[#e07a5f]" /> : bookings.length === 0 ? <p className="font-body text-[#3a4d68]">Aucune réservation.</p> : (
        <div className="space-y-3">
          {bookings.map((b) => (
            <Card key={b.id} className="p-4" >
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-head font-bold text-[#6f7d54]">{b.parent_name}</span>
                    <Badge status={b.status} />
                    {b.is_member && <span className="text-xs font-bold text-[#4a6742]">membre</span>}
                  </div>
                  <div className="font-body text-sm text-[#3a4d68]">{b.parent_email} · {b.total_slots} sessions · {b.amount}€</div>
                </div>
                <div className="flex items-center gap-2">
                  <select value={b.status} onChange={(e) => setStatus(b.id, e.target.value)} data-testid={`booking-status-${b.id}`} className="rounded-lg border border-[#6f7d54]/15 px-2 py-1.5 text-sm font-semibold">
                    {["pending", "paid", "cancelled", "expired"].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <select value={b.payment_method || "online"} onChange={(e) => setPay(b.id, e.target.value)} data-testid={`booking-payment-${b.id}`} title="Mode de paiement" className="rounded-lg border border-[#6f7d54]/15 px-2 py-1.5 text-sm font-semibold">
                    {Object.entries(PAY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <button onClick={() => setExpanded(expanded === b.id ? null : b.id)} className="text-sm font-bold text-[#2e8b9e]">Détails</button>
                  <button onClick={() => cancel(b.id)} data-testid={`booking-cancel-${b.id}`} title="Annuler" className="text-xs font-bold text-[#8a6a1f]">Annuler</button>
                  <button onClick={() => del(b.id)} data-testid={`booking-delete-${b.id}`} title="Supprimer" className="text-[#e63946]"><Trash2 size={16} /></button>
                </div>
              </div>
              {expanded === b.id && (
                <div className="mt-4 border-t border-[#6f7d54]/10 pt-4 space-y-3">
                  {(b.emergency_name || b.emergency_phone) && (
                    <div className="flex items-center gap-2 text-sm font-body text-[#b5232f]"><Phone size={14} /> Urgence : {b.emergency_name} {b.emergency_phone}</div>
                  )}
                  {b.children.map((c, i) => (
                    <div key={i} className="bg-[#fbf6ec] rounded-xl p-3">
                      <div className="font-head font-bold text-[#6f7d54] flex items-center gap-2 flex-wrap"><Baby size={15} /> {c.name}{c.age_group_fr ? <span className="text-sm font-normal text-[#2e8b9e]">· {c.age_group_fr}</span> : null}</div>
                      {c.dob ? <div className="text-xs text-[#3a4d68] mt-0.5">Né(e) le {c.dob}{c.age != null ? ` · ${c.age} ans` : ""}</div> : null}
                      {c.allergies ? <div className="text-sm text-[#cf6a50] font-semibold flex items-center gap-1 mt-1"><AlertTriangle size={13} /> Allergies : {c.allergies}</div> : null}
                      <div className="text-sm text-[#3a4d68] mt-1">{c.slots.map((s) => `${s.date} (${s.period === "morning" ? "matin" : "après-midi"})`).join(" · ")}</div>
                    </div>
                  ))}
                  {b.consents && (
                    <div data-testid={`booking-consents-${b.id}`} className="text-sm text-[#3a4d68] bg-[#eef4f5] rounded-xl p-3 space-y-0.5">
                      <div className="font-semibold text-[#6f7d54]">Autorisations</div>
                      <div>Droit à l'image : {b.consents.image_rights ? "Oui" : "Non"}</div>
                      <div>Activités extérieures : {b.consents.outdoor_activities ? "Oui" : "Non"}</div>
                      <div>Baignade : {b.consents.swimming ? "Oui" : "Non"}</div>
                      <div>Transport / conduite en cas d'urgence : {b.consents.emergency_transport ? "Oui" : "Non"}</div>
                      <div>Réside à Saint-Barthélemy : {b.consents.resident ? "Oui" : "Non"}</div>
                      {b.consents.authorized_pickup ? <div>Personnes autorisées : {b.consents.authorized_pickup}</div> : null}
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- MEMBERSHIPS ---------------- */
function MembershipsTab({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const load = useCallback(() => { api.get("/admin/memberships").then((r) => { setItems(r.data.memberships); setLoading(false); }).catch(() => setLoading(false)); }, [api]);
  useEffect(() => { load(); }, [load]);
  const markPaid = async (id) => { await api.post(`/admin/memberships/${id}/mark-paid`); load(); };
  const del = async (id) => { if (!window.confirm("Supprimer définitivement cette adhésion ?")) return; await api.delete(`/admin/memberships/${id}`); load(); };
  const setPay = async (id, pm) => { await api.put(`/admin/memberships/${id}`, { payment_method: pm }); load(); };
  const PAY_LABELS = { online: "Lien en ligne", virement: "Virement", especes: "Espèces" };
  return (
    <div data-testid="memberships-tab">
      <div className="flex items-center justify-between mb-5">
        <H2>Adhésions</H2>
        <button onClick={() => downloadCsv(api, "/admin/export/memberships.csv", "adhesions.csv")} className="inline-flex items-center gap-1.5 rounded-full bg-[#6f7d54] text-white font-bold text-sm px-4 py-2"><Download size={15} /> CSV</button>
      </div>
      {loading ? <Loader2 className="animate-spin text-[#e07a5f]" /> : items.length === 0 ? <p className="font-body text-[#3a4d68]">Aucune adhésion.</p> : (
        <div className="space-y-3">
          {items.map((m) => (
            <Card key={m.id} className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2"><span className="font-head font-bold text-[#6f7d54]">{m.member_name}</span><Badge status={m.status} /></div>
                  <div className="font-body text-sm text-[#3a4d68]">{m.member_email} · {m.member_phone || "—"} · {m.amount}€</div>
                  <div className="font-body text-xs text-[#3a4d68]/80 mt-0.5">
                    Adhésion le {(m.created_at || "").slice(0, 10) || "—"} · <span className="font-semibold text-[#4a6742]">Valable jusqu'au {m.valid_until || "—"}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <select value={m.payment_method || "online"} onChange={(e) => setPay(m.id, e.target.value)} data-testid={`membership-payment-${m.id}`} title="Mode de paiement" className="rounded-lg border border-[#6f7d54]/15 px-2 py-1.5 text-sm font-semibold">
                    {Object.entries(PAY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                  <button onClick={() => setExpanded(expanded === m.id ? null : m.id)} data-testid={`membership-fiche-${m.id}`} className="text-sm font-bold text-[#2e8b9e]">Fiche</button>
                  {m.status !== "paid" && <button onClick={() => markPaid(m.id)} data-testid={`mark-paid-${m.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#5e7d52] text-white font-bold text-sm px-4 py-2"><Check size={15} /> Marquer payé</button>}
                  <button onClick={() => del(m.id)} data-testid={`membership-delete-${m.id}`} title="Supprimer" className="text-[#e63946]"><Trash2 size={16} /></button>
                </div>
              </div>
              {expanded === m.id && (
                <div data-testid={`membership-details-${m.id}`} className="mt-4 border-t border-[#6f7d54]/10 pt-4 space-y-3 text-sm">
                  {m.member_address ? <div className="text-[#3a4d68]"><span className="font-semibold text-[#6f7d54]">Adresse :</span> {m.member_address}</div> : null}
                  {(m.emergency_name || m.emergency_phone) ? <div className="flex items-center gap-1 text-[#b5232f]"><Phone size={13} /> Urgence : {m.emergency_name} {m.emergency_phone}</div> : null}
                  {(m.children || []).length > 0 && (m.children || []).map((c, i) => (
                    <div key={i} className="bg-[#fbf6ec] rounded-xl p-3">
                      <div className="font-head font-bold text-[#6f7d54] flex items-center gap-2 flex-wrap"><Baby size={15} /> {c.name}{c.age_group_fr ? <span className="text-xs font-normal text-[#2e8b9e]">· {c.age_group_fr}</span> : null}</div>
                      {c.dob ? <div className="text-xs text-[#3a4d68] mt-0.5">Né(e) le {c.dob}{c.age != null ? ` · ${c.age} ans` : ""}</div> : null}
                      {c.medical ? <div className="text-[#cf6a50] font-semibold mt-1 flex items-center gap-1"><AlertTriangle size={13} /> Médical : {c.medical}</div> : null}
                      {c.interests ? <div className="text-[#3a4d68] mt-1">Aime : {c.interests}</div> : null}
                    </div>
                  ))}
                  {m.consents && (
                    <div className="bg-[#eef4f5] rounded-xl p-3 space-y-0.5 text-[#3a4d68]">
                      <div className="font-semibold text-[#6f7d54]">Autorisations</div>
                      <div>Droit à l'image & publication : {m.consents.image_rights ? "Oui" : "Non"}</div>
                      <div>Activités & sorties extérieures : {m.consents.outdoor_activities ? "Oui" : "Non"}</div>
                      <div>Baignade : {m.consents.swimming ? "Oui" : "Non"}</div>
                      <div>Transport / conduite en cas d'urgence : {m.consents.emergency_transport ? "Oui" : "Non"}</div>
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- CAMP DATES ---------------- */
function AnnouncementsTab({ api }) {
  const empty = { title_fr: "", title_en: "", text_fr: "", text_en: "", cta_label_fr: "", cta_label_en: "", cta_url: "", start_date: "", end_date: "", active: true };
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(() => { api.get("/admin/announcements").then((r) => { setItems(r.data.announcements || []); setLoading(false); }).catch(() => setLoading(false)); }, [api]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setError("");
    if (!draft.title_fr.trim()) { setError("Le titre (FR) est requis"); return; }
    try { await api.post("/admin/announcements", draft); setDraft(empty); load(); }
    catch (e) { setError(e?.response?.data?.detail || "Erreur"); }
  };
  const update = async (a, patch) => {
    const { id, order, ...rest } = a;
    await api.put(`/admin/announcements/${id}`, { ...rest, ...patch });
    load();
  };
  const del = async (id) => { if (!window.confirm("Supprimer cette annonce ?")) return; await api.delete(`/admin/announcements/${id}`); load(); };

  return (
    <div data-testid="announcements-tab">
      <H2>Annonces — Bandeau d'accueil</H2>
      <Card className="p-5 mb-5">
        <p className="font-head font-bold text-[#6f7d54] mb-3">Nouvelle annonce</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Titre (FR)</span><input value={draft.title_fr} onChange={(e) => setDraft({ ...draft, title_fr: e.target.value })} data-testid="ann-title-fr" className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Titre (EN)</span><input value={draft.title_en} onChange={(e) => setDraft({ ...draft, title_en: e.target.value })} data-testid="ann-title-en" className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Texte (FR)</span><textarea value={draft.text_fr} onChange={(e) => setDraft({ ...draft, text_fr: e.target.value })} rows={2} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Texte (EN)</span><textarea value={draft.text_en} onChange={(e) => setDraft({ ...draft, text_en: e.target.value })} rows={2} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Libellé bouton (FR)</span><input value={draft.cta_label_fr} onChange={(e) => setDraft({ ...draft, cta_label_fr: e.target.value })} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Libellé bouton (EN)</span><input value={draft.cta_label_en} onChange={(e) => setDraft({ ...draft, cta_label_en: e.target.value })} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Lien du bouton (ex. /kids-camp)</span><input value={draft.cta_url} onChange={(e) => setDraft({ ...draft, cta_url: e.target.value })} placeholder="/kids-camp ou https://…" className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <div className="flex gap-3">
            <label className="text-sm flex-1"><span className="block font-semibold text-[#3a4d68] mb-1">Début (optionnel)</span><input type="date" value={draft.start_date} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
            <label className="text-sm flex-1"><span className="block font-semibold text-[#3a4d68] mb-1">Fin (optionnel)</span><input type="date" value={draft.end_date} onChange={(e) => setDraft({ ...draft, end_date: e.target.value })} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          </div>
        </div>
        <div className="flex items-center gap-4 mt-3">
          <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} /> Active</label>
          <button onClick={add} data-testid="ann-add" className="inline-flex items-center gap-1.5 rounded-full bg-[#e07a5f] text-white font-bold text-sm px-4 py-2.5"><Plus size={16} /> Ajouter l'annonce</button>
        </div>
        {error && <p className="text-[#b5232f] text-sm font-semibold mt-2">{error}</p>}
      </Card>

      {loading ? <Loader2 className="animate-spin text-[#e07a5f]" /> : (
        <div className="space-y-3">
          {items.length === 0 && <p className="font-body text-[#3a4d68]/70 text-sm">Aucune annonce pour le moment.</p>}
          {items.map((a) => (
            <Card key={a.id} className={`p-4 ${a.active ? "" : "opacity-60"}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-head font-bold text-[#6f7d54]">{a.title_fr || "(sans titre)"}</p>
                  {a.text_fr && <p className="font-body text-sm text-[#3a4d68] mt-0.5">{a.text_fr}</p>}
                  <div className="flex flex-wrap gap-2 mt-2 text-xs text-[#3a4d68]/80">
                    {a.cta_url && <span className="rounded-full bg-[#2e8b9e]/10 text-[#2e8b9e] font-bold px-2.5 py-1">{a.cta_label_fr || "Bouton"} → {a.cta_url}</span>}
                    {(a.start_date || a.end_date) && <span className="rounded-full bg-[#6f7d54]/8 font-semibold px-2.5 py-1">{a.start_date || "…"} → {a.end_date || "…"}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={() => update(a, { active: !a.active })} data-testid={`ann-toggle-${a.id}`} className={`rounded-full px-3 py-1 text-xs font-bold ${a.active ? "bg-[#5e7d52]/15 text-[#4a6742]" : "bg-[#e63946]/12 text-[#b5232f]"}`}>{a.active ? "Active" : "Inactive"}</button>
                  <button onClick={() => del(a.id)} data-testid={`ann-del-${a.id}`} className="text-[#e63946]"><Trash2 size={16} /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function VacationPeriodsTab({ api }) {
  const empty = { name_fr: "", name_en: "", start_date: "", end_date: "", active: true };
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = useCallback(() => { api.get("/admin/vacation-periods").then((r) => { setItems(r.data.periods || []); setLoading(false); }).catch(() => setLoading(false)); }, [api]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setError("");
    if (!draft.name_fr.trim() || !draft.start_date || !draft.end_date) { setError("Nom (FR), date de début et de fin requis"); return; }
    try { await api.post("/admin/vacation-periods", draft); setDraft(empty); load(); }
    catch (e) { setError(e?.response?.data?.detail || "Erreur"); }
  };
  const update = async (p, patch) => { const { id, ...rest } = p; await api.put(`/admin/vacation-periods/${id}`, { ...rest, ...patch }); load(); };
  const del = async (id) => { if (!window.confirm("Supprimer cette période ?")) return; await api.delete(`/admin/vacation-periods/${id}`); load(); };

  return (
    <div data-testid="periods-tab">
      <H2>Périodes de vacances (Kids Camp)</H2>
      <p className="font-body text-sm text-[#3a4d68]/70 mb-4">Le parent choisit d'abord une période, puis voit le calendrier des dates comprises dans cette période. Modifiez le nom et les dates de chaque période ci-dessous. Pour ajouter ou retirer un jour précis (Kids Camp ou atelier), utilisez l'onglet « Dates ».</p>
      <Card className="p-5 mb-5">
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Nom (FR)</span><input value={draft.name_fr} onChange={(e) => setDraft({ ...draft, name_fr: e.target.value })} data-testid="period-name-fr" placeholder="ex. Noël 2026" className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Nom (EN)</span><input value={draft.name_en} onChange={(e) => setDraft({ ...draft, name_en: e.target.value })} placeholder="Christmas 2026" className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Début</span><input type="date" value={draft.start_date} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} data-testid="period-start" className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Fin</span><input type="date" value={draft.end_date} onChange={(e) => setDraft({ ...draft, end_date: e.target.value })} data-testid="period-end" className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
        </div>
        <div className="flex items-center gap-4 mt-3">
          <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={draft.active} onChange={(e) => setDraft({ ...draft, active: e.target.checked })} /> Active</label>
          <button onClick={add} data-testid="period-add" className="inline-flex items-center gap-1.5 rounded-full bg-[#e07a5f] text-white font-bold text-sm px-4 py-2.5"><Plus size={16} /> Ajouter la période</button>
        </div>
        {error && <p className="text-[#b5232f] text-sm font-semibold mt-2">{error}</p>}
      </Card>
      {loading ? <Loader2 className="animate-spin text-[#e07a5f]" /> : (
        <div className="space-y-3">
          {items.length === 0 && <p className="font-body text-[#3a4d68]/70 text-sm">Aucune période.</p>}
          {items.map((p) => (
            <PeriodRow key={p.id} p={p} onSave={(patch) => update(p, patch)} onToggle={() => update(p, { active: !p.active })} onDelete={() => del(p.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

function PeriodRow({ p, onSave, onToggle, onDelete }) {
  const [form, setForm] = useState({ name_fr: p.name_fr || "", name_en: p.name_en || "", start_date: p.start_date || "", end_date: p.end_date || "" });
  const [savedFlag, setSavedFlag] = useState(false);
  useEffect(() => { setForm({ name_fr: p.name_fr || "", name_en: p.name_en || "", start_date: p.start_date || "", end_date: p.end_date || "" }); }, [p]);
  const dirty = form.name_fr !== (p.name_fr || "") || form.name_en !== (p.name_en || "") || form.start_date !== (p.start_date || "") || form.end_date !== (p.end_date || "");
  const save = async () => { await onSave(form); setSavedFlag(true); setTimeout(() => setSavedFlag(false), 1800); };
  return (
    <Card className={`p-4 ${p.active ? "" : "opacity-60"}`} data-testid={`period-row-${p.id}`}>
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Nom (FR)</span><input value={form.name_fr} onChange={(e) => setForm({ ...form, name_fr: e.target.value })} data-testid={`period-edit-name-fr-${p.id}`} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
        <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Nom (EN)</span><input value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
        <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Début</span><input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid={`period-edit-start-${p.id}`} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
        <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Fin</span><input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid={`period-edit-end-${p.id}`} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <button onClick={save} disabled={!dirty} data-testid={`period-save-${p.id}`} className={`inline-flex items-center gap-1.5 rounded-full font-bold text-sm px-4 py-2 ${dirty ? "bg-[#5e7d52] text-white" : "bg-[#6f7d54]/10 text-[#3a4d68]/50 cursor-not-allowed"}`}><Check size={14} /> {savedFlag ? "Enregistré ✓" : "Enregistrer"}</button>
        <button onClick={onToggle} data-testid={`period-toggle-${p.id}`} className={`rounded-full px-3 py-1 text-xs font-bold ${p.active ? "bg-[#5e7d52]/15 text-[#4a6742]" : "bg-[#e63946]/12 text-[#b5232f]"}`}>{p.active ? "Active" : "Inactive"}</button>
        <button onClick={onDelete} data-testid={`period-del-${p.id}`} className="text-[#e63946] ml-auto"><Trash2 size={16} /></button>
      </div>
    </Card>
  );
}

function UpcomingEventTab({ api }) {
  const empty = { title_fr: "", title_en: "", date_label_fr: "", date_label_en: "", text_fr: "", text_en: "", cta_label_fr: "", cta_label_en: "", cta_url: "", active: false };
  const [ev, setEv] = useState(empty);
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(false);
  const load = useCallback(() => { api.get("/admin/upcoming-event").then((r) => { setEv({ ...empty, ...(r.data || {}) }); setLoading(false); }).catch(() => setLoading(false)); }, [api]);
  useEffect(() => { load(); }, [load]);
  const f = (k, v) => setEv({ ...ev, [k]: v });
  const save = async () => { await api.put("/admin/upcoming-event", ev); setSaved(true); setTimeout(() => setSaved(false), 2000); };

  if (loading) return <Loader2 className="animate-spin text-[#e07a5f]" />;
  return (
    <div data-testid="upcoming-tab">
      <H2>Bandeau « À venir » (accueil)</H2>
      <p className="font-body text-sm text-[#3a4d68]/70 mb-4">Affiche le prochain événement de l'association en haut de la page d'accueil. Décochez « Actif » pour le masquer.</p>
      <Card className="p-5 space-y-4">
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Titre (FR)</span><input value={ev.title_fr} onChange={(e) => f("title_fr", e.target.value)} data-testid="upcoming-title-fr" className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Titre (EN)</span><input value={ev.title_en} onChange={(e) => f("title_en", e.target.value)} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Date (texte, FR)</span><input value={ev.date_label_fr} onChange={(e) => f("date_label_fr", e.target.value)} placeholder="ex. Samedi 12 septembre 2026" className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Date (texte, EN)</span><input value={ev.date_label_en} onChange={(e) => f("date_label_en", e.target.value)} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Description (FR)</span><textarea value={ev.text_fr} onChange={(e) => f("text_fr", e.target.value)} rows={2} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Description (EN)</span><textarea value={ev.text_en} onChange={(e) => f("text_en", e.target.value)} rows={2} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Libellé bouton (FR)</span><input value={ev.cta_label_fr} onChange={(e) => f("cta_label_fr", e.target.value)} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Libellé bouton (EN)</span><input value={ev.cta_label_en} onChange={(e) => f("cta_label_en", e.target.value)} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm sm:col-span-2"><span className="block font-semibold text-[#3a4d68] mb-1">Lien du bouton (ex. /ateliers)</span><input value={ev.cta_url} onChange={(e) => f("cta_url", e.target.value)} placeholder="/ateliers ou https://…" className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
        </div>
        <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={ev.active} onChange={(e) => f("active", e.target.checked)} data-testid="upcoming-active" /> Actif (visible sur l'accueil)</label>
        <button onClick={save} data-testid="upcoming-save" className="inline-flex items-center gap-1.5 rounded-full bg-[#5e7d52] text-white font-bold text-sm px-5 py-2.5"><Check size={15} /> {saved ? "Enregistré ✓" : "Enregistrer"}</button>
      </Card>
    </div>
  );
}

function PricingTab({ api }) {
  const [p, setP] = useState(null);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  const load = useCallback(() => { api.get("/admin/pricing").then((r) => setP(r.data)).catch(() => setErr("Erreur de chargement")); }, [api]);
  useEffect(() => { load(); }, [load]);
  const f = (k, v) => setP({ ...p, [k]: v });
  const save = async () => {
    setErr("");
    const payload = {
      membership_price: parseFloat(p.membership_price),
      member_price: parseFloat(p.member_price),
      non_member_price: parseFloat(p.non_member_price),
      member_discount_price: parseFloat(p.member_discount_price),
      discount_threshold: parseInt(p.discount_threshold),
    };
    if (Object.values(payload).some((v) => isNaN(v))) { setErr("Tous les champs doivent être numériques"); return; }
    try { await api.put("/admin/pricing", payload); setSaved(true); setTimeout(() => setSaved(false), 2000); }
    catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
  };
  if (!p) return <Loader2 className="animate-spin text-[#e07a5f]" />;
  const FIELD = (k, label, help) => (
    <label className="text-sm block"><span className="block font-semibold text-[#3a4d68] mb-1">{label}</span>
      <input type="number" step="1" value={p[k]} onChange={(e) => f(k, e.target.value)} data-testid={`price-${k}`} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" />
      {help && <span className="block text-xs text-[#3a4d68]/60 mt-1">{help}</span>}
    </label>
  );
  return (
    <div data-testid="pricing-tab">
      <H2>Tarifs</H2>
      <p className="font-body text-sm text-[#3a4d68]/70 mb-4">Ces tarifs sont la <b>source unique</b> : le site public et le parcours de réservation les utilisent automatiquement.</p>
      <Card className="p-5 space-y-4 max-w-2xl">
        <div className="grid sm:grid-cols-2 gap-4">
          {FIELD("membership_price", "Adhésion annuelle (€)")}
          {FIELD("member_price", "Session adhérent (€)")}
          {FIELD("non_member_price", "Session non-adhérent (€)")}
          {FIELD("member_discount_price", "Session adhérent — tarif carnet (€)")}
          {FIELD("discount_threshold", "Seuil carnet (sessions / enfant)", "Nb de sessions pour un même enfant qui déclenche le tarif carnet.")}
        </div>
        {err && <p className="text-[#b5232f] text-sm font-semibold">{err}</p>}
        <button onClick={save} data-testid="pricing-save" className="inline-flex items-center gap-1.5 rounded-full bg-[#5e7d52] text-white font-bold text-sm px-5 py-2.5"><Check size={15} /> {saved ? "Enregistré ✓" : "Enregistrer les tarifs"}</button>
      </Card>
    </div>
  );
}

function AgeGroupsTab({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [nw, setNw] = useState({ name_fr: "", name_en: "", min_age: "", max_age: "", description_fr: "", description_en: "", active: true });
  const load = useCallback(() => { api.get("/admin/age-groups").then((r) => { setItems(r.data.age_groups || []); setLoading(false); }).catch(() => setLoading(false)); }, [api]);
  useEffect(() => { load(); }, [load]);

  const seedIfNeeded = async () => {
    // if defaults are surfaced (id null), persist them so they become editable
    const pending = items.filter((g) => !g.id);
    for (const g of pending) {
      const { id, ...rest } = g;
      await api.post("/admin/age-groups", rest);
    }
    load();
  };
  useEffect(() => { if (items.length && items.some((g) => !g.id)) seedIfNeeded(); /* eslint-disable-next-line */ }, [items]);

  const add = async () => {
    setErr("");
    const min_age = parseInt(nw.min_age), max_age = parseInt(nw.max_age);
    if (!nw.name_fr.trim() || isNaN(min_age) || isNaN(max_age)) { setErr("Nom et âges min/max requis"); return; }
    if (min_age > max_age) { setErr("Âge min > âge max"); return; }
    await api.post("/admin/age-groups", { ...nw, min_age, max_age, order: items.length });
    setNw({ name_fr: "", name_en: "", min_age: "", max_age: "", description_fr: "", description_en: "", active: true });
    load();
  };
  const save = async (g) => {
    await api.put(`/admin/age-groups/${g.id}`, {
      name_fr: g.name_fr, name_en: g.name_en, min_age: parseInt(g.min_age), max_age: parseInt(g.max_age),
      description_fr: g.description_fr || "", description_en: g.description_en || "", active: g.active !== false, order: g.order || 0,
    });
    load();
  };
  const del = async (id) => { if (!window.confirm("Supprimer cette tranche d'âge ?")) return; await api.delete(`/admin/age-groups/${id}`); load(); };
  const upd = (id, k, v) => setItems((p) => p.map((g) => (g.id === id ? { ...g, [k]: v } : g)));

  return (
    <div data-testid="ages-tab">
      <H2>Tranches d'âge</H2>
      <p className="font-body text-sm text-[#3a4d68]/70 mb-4">Ces groupes sont la <b>source unique</b> : la réservation et l'adhésion les utilisent pour classer chaque enfant selon son âge.</p>
      {loading ? <Loader2 className="animate-spin text-[#e07a5f]" /> : (
        <div className="space-y-3 max-w-3xl">
          {items.filter((g) => g.id).map((g) => (
            <Card key={g.id} className="p-4">
              <div className="grid sm:grid-cols-[1fr_1fr_80px_80px] gap-3 items-end">
                <label className="text-sm block"><span className="block font-semibold text-[#3a4d68] mb-1">Nom (FR)</span>
                  <input value={g.name_fr} onChange={(e) => upd(g.id, "name_fr", e.target.value)} data-testid={`age-name-fr-${g.id}`} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
                <label className="text-sm block"><span className="block font-semibold text-[#3a4d68] mb-1">Nom (EN)</span>
                  <input value={g.name_en || ""} onChange={(e) => upd(g.id, "name_en", e.target.value)} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
                <label className="text-sm block"><span className="block font-semibold text-[#3a4d68] mb-1">Âge min</span>
                  <input type="number" value={g.min_age} onChange={(e) => upd(g.id, "min_age", e.target.value)} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
                <label className="text-sm block"><span className="block font-semibold text-[#3a4d68] mb-1">Âge max</span>
                  <input type="number" value={g.max_age} onChange={(e) => upd(g.id, "max_age", e.target.value)} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <button onClick={() => save(g)} data-testid={`age-save-${g.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#5e7d52] text-white font-bold text-sm px-4 py-2"><Check size={14} /> Enregistrer</button>
                <button onClick={() => upd(g.id, "active", !(g.active !== false)) || save({ ...g, active: !(g.active !== false) })} data-testid={`age-toggle-${g.id}`} className={`rounded-full px-3 py-1 text-xs font-bold ${g.active !== false ? "bg-[#5e7d52]/15 text-[#4a6742]" : "bg-[#e63946]/12 text-[#b5232f]"}`}>{g.active !== false ? "Actif" : "Inactif"}</button>
                <button onClick={() => del(g.id)} data-testid={`age-del-${g.id}`} className="text-[#e63946] ml-auto"><Trash2 size={16} /></button>
              </div>
            </Card>
          ))}
          <Card className="p-4 border-dashed">
            <p className="font-head font-bold text-[#6f7d54] mb-3">Ajouter une tranche d'âge</p>
            <div className="grid sm:grid-cols-[1fr_1fr_80px_80px] gap-3 items-end">
              <label className="text-sm block"><span className="block font-semibold text-[#3a4d68] mb-1">Nom (FR)</span>
                <input value={nw.name_fr} onChange={(e) => setNw({ ...nw, name_fr: e.target.value })} data-testid="age-new-name-fr" placeholder="Les Aventuriers" className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
              <label className="text-sm block"><span className="block font-semibold text-[#3a4d68] mb-1">Nom (EN)</span>
                <input value={nw.name_en} onChange={(e) => setNw({ ...nw, name_en: e.target.value })} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
              <label className="text-sm block"><span className="block font-semibold text-[#3a4d68] mb-1">Âge min</span>
                <input type="number" value={nw.min_age} onChange={(e) => setNw({ ...nw, min_age: e.target.value })} data-testid="age-new-min" className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
              <label className="text-sm block"><span className="block font-semibold text-[#3a4d68] mb-1">Âge max</span>
                <input type="number" value={nw.max_age} onChange={(e) => setNw({ ...nw, max_age: e.target.value })} data-testid="age-new-max" className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
            </div>
            {err && <p className="text-[#b5232f] text-sm font-semibold mt-2">{err}</p>}
            <button onClick={add} data-testid="age-add" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#e07a5f] text-white font-bold text-sm px-4 py-2.5"><Plus size={16} /> Ajouter</button>
          </Card>
        </div>
      )}
    </div>
  );
}

const EMPTY_EVENT = {
  title_fr: "", title_en: "", subtitle_fr: "", subtitle_en: "", age_group_fr: "", age_group_en: "",
  start_date: "", end_date: "", dates_label_fr: "", dates_label_en: "", schedule_fr: "", schedule_en: "",
  capacity: 12, full_package: true, member_price: "", non_member_price: "", promo_member_price: "", promo_non_member_price: "",
  promo_label_fr: "", promo_label_en: "", included_fr: "", included_en: "", image: "", status: "draft",
};

function SpecialEventsTab({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // event object or null
  const [err, setErr] = useState("");
  const [uploadingImg, setUploadingImg] = useState(false);
  const load = useCallback(() => { api.get("/admin/special-events").then((r) => { setItems(r.data.events || []); setLoading(false); }).catch(() => setLoading(false)); }, [api]);
  useEffect(() => { load(); }, [load]);

  const toNum = (v) => (v === "" || v === null || v === undefined ? null : parseFloat(v));
  const payloadFrom = (e) => ({
    ...e,
    capacity: parseInt(e.capacity) || 0,
    member_price: parseFloat(e.member_price) || 0,
    non_member_price: parseFloat(e.non_member_price) || 0,
    promo_member_price: toNum(e.promo_member_price),
    promo_non_member_price: toNum(e.promo_non_member_price),
    full_package: e.full_package !== false,
  });

  const saveEvent = async () => {
    setErr("");
    if (!editing.title_fr.trim()) { setErr("Le titre (FR) est requis"); return; }
    try {
      if (editing.id) await api.put(`/admin/special-events/${editing.id}`, payloadFrom(editing));
      else await api.post("/admin/special-events", payloadFrom(editing));
      setEditing(null); load();
    } catch (e) { setErr(e?.response?.data?.detail || "Erreur"); }
  };
  const del = async (id) => { if (!window.confirm("Supprimer cet événement ?")) return; await api.delete(`/admin/special-events/${id}`); load(); };
  const togglePublish = async (e) => { await api.put(`/admin/special-events/${e.id}`, payloadFrom({ ...e, status: e.status === "published" ? "draft" : "published" })); load(); };
  const f = (k, v) => setEditing((p) => ({ ...p, [k]: v }));

  const uploadImage = async (file) => {
    if (!file) return;
    if (!editing.id) { setErr("Enregistrez d'abord l'événement, puis ajoutez l'image."); return; }
    setErr(""); setUploadingImg(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/admin/special-events/${editing.id}/image`, fd);
      setEditing((p) => ({ ...p, image: r.data.image }));
      setItems((list) => list.map((e) => (e.id === editing.id ? { ...e, image: r.data.image } : e)));
    } catch (e) { setErr(e?.response?.data?.detail || "Échec de l'upload"); }
    setUploadingImg(false);
  };

  const F = (k, label, type = "text", placeholder = "") => (
    <label className="text-sm block"><span className="block font-semibold text-[#3a4d68] mb-1">{label}</span>
      <input type={type} value={editing[k] ?? ""} onChange={(e) => f(k, e.target.value)} data-testid={`ev-${k}`} placeholder={placeholder} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" />
    </label>
  );
  const TA = (k, label, placeholder = "") => (
    <label className="text-sm block"><span className="block font-semibold text-[#3a4d68] mb-1">{label}</span>
      <textarea value={editing[k] ?? ""} onChange={(e) => f(k, e.target.value)} data-testid={`ev-${k}`} placeholder={placeholder} rows={4} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" />
    </label>
  );

  if (editing) {
    return (
      <div data-testid="event-editor">
        <div className="flex items-center justify-between mb-4">
          <H2>{editing.id ? "Modifier l'événement" : "Nouvel événement"}</H2>
          <button onClick={() => setEditing(null)} className="text-sm font-bold text-[#3a4d68]"><X size={16} className="inline" /> Fermer</button>
        </div>
        <Card className="p-5 space-y-4 max-w-3xl">
          <div className="grid sm:grid-cols-2 gap-4">
            {F("title_fr", "Titre (FR)")}
            {F("title_en", "Titre (EN)")}
            {F("subtitle_fr", "Sous-titre (FR)")}
            {F("subtitle_en", "Sous-titre (EN)")}
            {F("age_group_fr", "Tranche d'âge (FR)", "text", "Les Aventuriers (14-17 ans)")}
            {F("age_group_en", "Tranche d'âge (EN)")}
            {F("start_date", "Date de début", "date")}
            {F("end_date", "Date de fin", "date")}
            {F("dates_label_fr", "Libellé dates (FR)", "text", "Du 28 au 31 octobre 2026")}
            {F("dates_label_en", "Libellé dates (EN)")}
            {F("schedule_fr", "Horaires (FR)", "text", "4 jours complets · 8h30-17h00")}
            {F("schedule_en", "Horaires (EN)")}
            {F("capacity", "Capacité", "number")}
          </div>
          <label className="flex items-center gap-2 text-sm font-semibold text-[#3a4d68]">
            <input type="checkbox" checked={editing.full_package !== false} onChange={(e) => f("full_package", e.target.checked)} data-testid="ev-full_package" />
            Forfait complet obligatoire (réservation des {editing.dates_label_fr || "jours"} en bloc)
          </label>
          <div className="border-t border-[#6f7d54]/10 pt-4">
            <p className="font-head font-bold text-[#6f7d54] mb-3">Tarifs</p>
            <div className="grid sm:grid-cols-2 gap-4">
              {F("member_price", "Prix adhérent (€)", "number")}
              {F("non_member_price", "Prix non-adhérent (€)", "number")}
              {F("promo_member_price", "Promo adhérent (€) — optionnel", "number")}
              {F("promo_non_member_price", "Promo non-adhérent (€) — optionnel", "number")}
              {F("promo_label_fr", "Libellé promo (FR)", "text", "Offre de lancement")}
              {F("promo_label_en", "Libellé promo (EN)")}
            </div>
          </div>
          <div className="border-t border-[#6f7d54]/10 pt-4 grid sm:grid-cols-2 gap-4">
            {TA("included_fr", "Ce qui est inclus (FR) — une ligne par élément")}
            {TA("included_en", "Ce qui est inclus (EN)")}
          </div>
          {F("image", "Image (URL) — optionnel")}
          <div className="border-t border-[#6f7d54]/10 pt-4">
            <p className="font-head font-bold text-[#6f7d54] mb-2">Image d'illustration</p>
            {editing.image ? (
              <img src={editing.image.startsWith("http") ? editing.image : `${process.env.REACT_APP_BACKEND_URL}${editing.image}`} alt="" className="w-full max-w-sm h-40 object-cover rounded-xl mb-3 border border-[#6f7d54]/10" />
            ) : <p className="text-sm text-[#3a4d68]/60 mb-3">Aucune image pour le moment.</p>}
            {editing.id ? (
              <label className="inline-flex items-center gap-2 rounded-full bg-[#2e8b9e] text-white font-bold text-sm px-4 py-2 cursor-pointer">
                {uploadingImg ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} {editing.image ? "Remplacer l'image" : "Téléverser une image"}
                <input type="file" accept="image/jpeg,image/png,image/webp" data-testid="ev-image-upload" className="hidden" onChange={(e) => uploadImage(e.target.files?.[0])} disabled={uploadingImg} />
              </label>
            ) : <p className="text-sm text-[#8a6a1f]">Enregistrez l'événement pour pouvoir téléverser une image.</p>}
          </div>
          {err && <p className="text-[#b5232f] text-sm font-semibold">{err}</p>}
          <div className="flex items-center gap-2">
            <button onClick={saveEvent} data-testid="ev-save" className="inline-flex items-center gap-1.5 rounded-full bg-[#5e7d52] text-white font-bold text-sm px-5 py-2.5"><Check size={15} /> Enregistrer</button>
            <span className="text-sm text-[#3a4d68]/70">Statut : <b>{editing.status === "published" ? "Publié" : "Brouillon"}</b> (bouton Publier dans la liste)</span>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="specialevents-tab">
      <div className="flex items-center justify-between mb-4">
        <H2>Événements spéciaux</H2>
        <button onClick={() => setEditing({ ...EMPTY_EVENT })} data-testid="ev-new" className="inline-flex items-center gap-1.5 rounded-full bg-[#e07a5f] text-white font-bold text-sm px-4 py-2.5"><Plus size={16} /> Nouvel événement</button>
      </div>
      <p className="font-body text-sm text-[#3a4d68]/70 mb-4">Forfaits multi-jours et programmes ponctuels (ex. St Barth Express). Les événements <b>publiés</b> apparaissent sur la page publique « Événements ».</p>
      {loading ? <Loader2 className="animate-spin text-[#e07a5f]" /> : items.length === 0 ? <p className="font-body text-[#3a4d68]">Aucun événement.</p> : (
        <div className="space-y-3">
          {items.map((e) => (
            <Card key={e.id} className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-head font-bold text-[#6f7d54]">{e.title_fr}</span>
                    <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${e.status === "published" ? "bg-[#5e7d52]/15 text-[#4a6742]" : "bg-[#e0b36a]/25 text-[#8a6a1f]"}`}>{e.status === "published" ? "Publié" : "Brouillon"}</span>
                  </div>
                  <div className="font-body text-sm text-[#3a4d68] mt-0.5">{e.dates_label_fr} · {e.age_group_fr}</div>
                  <div className="font-body text-sm text-[#3a4d68]/70">
                    Adhérent {e.promo_member_price != null ? <><s>{e.member_price}€</s> <b className="text-[#cf6a50]">{e.promo_member_price}€</b></> : `${e.member_price}€`} · Non-adhérent {e.promo_non_member_price != null ? <><s>{e.non_member_price}€</s> <b className="text-[#cf6a50]">{e.promo_non_member_price}€</b></> : `${e.non_member_price}€`}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => togglePublish(e)} data-testid={`ev-publish-${e.id}`} className={`rounded-full px-3 py-1.5 text-xs font-bold ${e.status === "published" ? "bg-[#e0b36a]/25 text-[#8a6a1f]" : "bg-[#5e7d52] text-white"}`}>{e.status === "published" ? "Dépublier" : "Publier"}</button>
                  <button onClick={() => setEditing({ ...e, member_price: e.member_price ?? "", non_member_price: e.non_member_price ?? "", promo_member_price: e.promo_member_price ?? "", promo_non_member_price: e.promo_non_member_price ?? "" })} data-testid={`ev-edit-${e.id}`} className="text-sm font-bold text-[#2e8b9e]">Modifier</button>
                  <button onClick={() => del(e.id)} data-testid={`ev-del-${e.id}`} className="text-[#e63946]"><Trash2 size={16} /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function DocumentsTab({ api }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nw, setNw] = useState({ title: "", category: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = useCallback(() => { api.get("/admin/documents").then((r) => { setDocs(r.data.documents || []); setLoading(false); }).catch(() => setLoading(false)); }, [api]);
  useEffect(() => { load(); }, [load]);

  const upload = async () => {
    setErr("");
    if (!file) { setErr("Choisissez un fichier (PDF, JPG ou PNG)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", nw.title || file.name);
      fd.append("category", nw.category);
      await api.post("/admin/documents", fd);
      setNw({ title: "", category: "" }); setFile(null);
      load();
    } catch (e) { setErr(e?.response?.data?.detail || "Échec de l'upload"); }
    setBusy(false);
  };
  const download = async (d) => {
    try {
      const r = await api.get(`/admin/documents/${d.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { setErr("Téléchargement impossible"); }
  };
  const del = async (id) => { if (!window.confirm("Supprimer ce document ?")) return; await api.delete(`/admin/documents/${id}`); load(); };
  const fmtSize = (s) => (s > 1048576 ? `${(s / 1048576).toFixed(1)} Mo` : `${Math.max(1, Math.round(s / 1024))} Ko`);

  const shared = docs.filter((d) => d.kind === "shared");
  const parentDocs = docs.filter((d) => d.kind === "parent");

  const Row = ({ d, showOwner }) => (
    <Card key={d.id} className="p-3.5 flex items-center justify-between gap-3">
      <div className="min-w-0 flex items-center gap-3">
        <FileText size={20} className="text-[#2e8b9e] shrink-0" />
        <div className="min-w-0">
          <div className="font-head font-bold text-[#6f7d54] truncate">{d.title}</div>
          <div className="text-xs text-[#3a4d68]/70 truncate">{d.category ? `${d.category} · ` : ""}{d.original_filename} · {fmtSize(d.size)}{showOwner && d.owner_email ? ` · ${d.owner_email}` : ""}</div>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <button onClick={() => download(d)} data-testid={`doc-download-${d.id}`} className="inline-flex items-center gap-1.5 rounded-full bg-[#6f7d54] text-white font-bold text-xs px-3 py-2"><Download size={14} /> Ouvrir</button>
        <button onClick={() => del(d.id)} data-testid={`doc-del-${d.id}`} className="text-[#e63946]"><Trash2 size={16} /></button>
      </div>
    </Card>
  );

  return (
    <div data-testid="documents-tab">
      <H2>Documents</H2>
      <p className="font-body text-sm text-[#3a4d68]/70 mb-4">Publiez les documents de l'association (fiche de renseignements, projet éducatif…) consultables par tous les parents, et consultez les documents déposés par chaque parent.</p>

      <Card className="p-5 max-w-2xl mb-8 border-dashed">
        <p className="font-head font-bold text-[#6f7d54] mb-3 flex items-center gap-2"><Upload size={17} /> Publier un document commun</p>
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-sm block"><span className="block font-semibold text-[#3a4d68] mb-1">Titre</span>
            <input value={nw.title} onChange={(e) => setNw({ ...nw, title: e.target.value })} data-testid="doc-title" placeholder="Fiche de renseignements 2026-2027" className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm block"><span className="block font-semibold text-[#3a4d68] mb-1">Catégorie (optionnel)</span>
            <input value={nw.category} onChange={(e) => setNw({ ...nw, category: e.target.value })} data-testid="doc-category" placeholder="Adhésion" className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
        </div>
        <input type="file" accept="application/pdf,image/jpeg,image/png" data-testid="doc-file" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-3 block text-sm" />
        {err && <p className="text-[#b5232f] text-sm font-semibold mt-2">{err}</p>}
        <button onClick={upload} disabled={busy} data-testid="doc-upload" className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#e07a5f] text-white font-bold text-sm px-4 py-2.5 disabled:opacity-50">{busy ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Publier</button>
      </Card>

      {loading ? <Loader2 className="animate-spin text-[#e07a5f]" /> : (
        <div className="space-y-8 max-w-2xl">
          <div>
            <p className="font-head font-bold text-[#6f7d54] mb-3">Documents de l'association ({shared.length})</p>
            {shared.length === 0 ? <p className="font-body text-sm text-[#3a4d68]/60">Aucun document publié.</p> : <div className="space-y-2">{shared.map((d) => <Row key={d.id} d={d} />)}</div>}
          </div>
          <div>
            <p className="font-head font-bold text-[#6f7d54] mb-3">Documents déposés par les parents ({parentDocs.length})</p>
            {parentDocs.length === 0 ? <p className="font-body text-sm text-[#3a4d68]/60">Aucun document déposé par les parents.</p> : <div className="space-y-2">{parentDocs.map((d) => <Row key={d.id} d={d} showOwner />)}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function CampDatesTab({ api }) {
  const [items, setItems] = useState([]);
  const [newDate, setNewDate] = useState({ date: "", capacity: 6, morning: true, afternoon: true, category: "camp", theme: "" });
  const [gen, setGen] = useState({ category: "atelier", weekdays: [2, 5], start_date: "", end_date: "", morning: true, afternoon: true, capacity: 6, theme: "" });
  const [genMsg, setGenMsg] = useState("");
  const [genBusy, setGenBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = useCallback(() => { api.get("/admin/camp-dates").then((r) => { setItems(r.data.camp_dates); setLoading(false); }).catch(() => setLoading(false)); }, [api]);
  useEffect(() => { load(); }, [load]);

  const WEEKDAYS = [["Lun", 0], ["Mar", 1], ["Mer", 2], ["Jeu", 3], ["Ven", 4], ["Sam", 5], ["Dim", 6]];
  const toggleWeekday = (wd) => setGen((g) => ({ ...g, weekdays: g.weekdays.includes(wd) ? g.weekdays.filter((x) => x !== wd) : [...g.weekdays, wd] }));
  const generate = async () => {
    setGenMsg("");
    if (!gen.start_date || !gen.end_date) { setGenMsg("Choisissez une date de début et de fin"); return; }
    if (gen.weekdays.length === 0) { setGenMsg("Sélectionnez au moins un jour"); return; }
    setGenBusy(true);
    try {
      const r = await api.post("/admin/generate-dates", gen);
      setGenMsg(`${r.data.created} date(s) créée(s), ${r.data.skipped} déjà existante(s) ignorée(s).`);
      load();
    } catch (e) { setGenMsg(e?.response?.data?.detail || "Erreur"); }
    finally { setGenBusy(false); }
  };

  const add = async () => {
    setError("");
    if (!newDate.date) { setError("Choisissez une date"); return; }
    const payload = newDate.category === "atelier" ? { ...newDate, morning: true, afternoon: true, closed: false } : { ...newDate, closed: false };
    try { await api.post("/admin/camp-dates", payload); setNewDate({ date: "", capacity: 6, morning: true, afternoon: true, category: newDate.category, theme: "" }); load(); }
    catch (e) { setError(e?.response?.data?.detail || "Erreur"); }
  };
  const update = async (cd, patch) => { await api.put(`/admin/camp-dates/${cd.id}`, { ...cd, ...patch }); load(); };
  const del = async (id) => { if (!window.confirm("Supprimer cette date ?")) return; await api.delete(`/admin/camp-dates/${id}`); load(); };

  const camps = items.filter((c) => (c.category || "camp") !== "atelier");
  const ateliers = items.filter((c) => (c.category || "camp") === "atelier");

  const DateCard = (cd) => (
    <Card key={cd.id} className={`p-4 ${cd.closed ? "opacity-60" : ""}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-head font-bold text-[#6f7d54]">{cd.date}</span>
        <button onClick={() => del(cd.id)} data-testid={`del-campdate-${cd.date}`} className="text-[#e63946]"><Trash2 size={15} /></button>
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        <button onClick={() => update(cd, { morning: !cd.morning })} className={`rounded-full px-3 py-1 font-bold ${cd.morning ? "bg-[#2e8b9e] text-white" : "bg-[#6f7d54]/8 text-[#3a4d68]"}`}>Matin</button>
        <button onClick={() => update(cd, { afternoon: !cd.afternoon })} className={`rounded-full px-3 py-1 font-bold ${cd.afternoon ? "bg-[#2e8b9e] text-white" : "bg-[#6f7d54]/8 text-[#3a4d68]"}`}>Après-midi</button>
        <label className="flex items-center gap-1 font-semibold text-[#3a4d68]">Places <input type="number" min="1" value={cd.capacity} onChange={(e) => update(cd, { capacity: parseInt(e.target.value) || 1 })} className="w-14 rounded border border-[#6f7d54]/15 px-1 py-0.5" /></label>
        <button onClick={() => update(cd, { closed: !cd.closed })} className={`rounded-full px-3 py-1 font-bold ${cd.closed ? "bg-[#e63946]/15 text-[#b5232f]" : "bg-[#5e7d52]/15 text-[#4a6742]"}`}>{cd.closed ? "Fermé" : "Ouvert"}</button>
      </div>
      {(cd.category || "camp") === "atelier" && (
        <label className="block mt-2 text-xs"><span className="block font-semibold text-[#3a4d68] mb-1">Thème de l'atelier</span>
          <input defaultValue={cd.theme || ""} onBlur={(e) => { if (e.target.value !== (cd.theme || "")) update(cd, { theme: e.target.value }); }} data-testid={`atelier-theme-${cd.date}`} placeholder="ex. Poterie & argile" className="w-full rounded border border-[#6f7d54]/15 px-2 py-1" /></label>
      )}
    </Card>
  );

  return (
    <div data-testid="campdates-tab">
      <H2>Dates — Camp & Ateliers</H2>

      <Card className="p-5 mb-5 border-2 border-[#2e8b9e]/25 bg-[#2e8b9e]/5">
        <p className="font-head font-bold text-[#2e8b9e] mb-1 flex items-center gap-2"><CalendarRange size={18} /> Générateur de dates</p>
        <p className="font-body text-sm text-[#3a4d68]/70 mb-4">Créez en masse des dates récurrentes sur une période (les dates déjà existantes sont ignorées).</p>
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Type</span>
            <select value={gen.category} onChange={(e) => setGen({ ...gen, category: e.target.value })} data-testid="gen-category" className="rounded-xl border border-[#6f7d54]/15 px-3 py-2">
              <option value="atelier">Atelier</option>
              <option value="camp">Kids Camp</option>
            </select>
          </label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Du</span><input type="date" value={gen.start_date} onChange={(e) => setGen({ ...gen, start_date: e.target.value })} data-testid="gen-start" className="rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Au</span><input type="date" value={gen.end_date} onChange={(e) => setGen({ ...gen, end_date: e.target.value })} data-testid="gen-end" className="rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Places</span><input type="number" min="1" value={gen.capacity} onChange={(e) => setGen({ ...gen, capacity: parseInt(e.target.value) || 1 })} className="w-20 rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
        </div>
        <div className="mt-3">
          <span className="block font-semibold text-sm text-[#3a4d68] mb-1.5">Jours de la semaine</span>
          <div className="flex flex-wrap gap-2">
            {WEEKDAYS.map(([lbl, wd]) => (
              <button key={wd} onClick={() => toggleWeekday(wd)} data-testid={`gen-weekday-${wd}`} className={`rounded-full px-3 py-1.5 text-xs font-bold border transition-all ${gen.weekdays.includes(wd) ? "bg-[#2e8b9e] text-white border-[#2e8b9e]" : "bg-white text-[#2e8b9e] border-[#2e8b9e]/30"}`}>{lbl}</button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-4 mt-3">
          <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={gen.morning} onChange={(e) => setGen({ ...gen, morning: e.target.checked })} data-testid="gen-morning" /> Matin</label>
          <label className="text-sm flex items-center gap-2"><input type="checkbox" checked={gen.afternoon} onChange={(e) => setGen({ ...gen, afternoon: e.target.checked })} data-testid="gen-afternoon" /> Après-midi</label>
          {gen.category === "atelier" && (
            <label className="text-sm flex items-center gap-2">Thème <input value={gen.theme} onChange={(e) => setGen({ ...gen, theme: e.target.value })} placeholder="optionnel" className="rounded-xl border border-[#6f7d54]/15 px-3 py-1.5" /></label>
          )}
          <button onClick={generate} disabled={genBusy} data-testid="gen-run" className="inline-flex items-center gap-1.5 rounded-full bg-[#2e8b9e] text-white font-bold text-sm px-5 py-2.5 disabled:opacity-50">{genBusy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />} Générer les dates</button>
        </div>
        {genMsg && <p data-testid="gen-msg" className="text-sm font-semibold mt-2 text-[#2e5b6b]">{genMsg}</p>}
      </Card>

      <Card className="p-4 mb-5">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Type</span>
            <select value={newDate.category} onChange={(e) => setNewDate({ ...newDate, category: e.target.value })} data-testid="new-campdate-category" className="rounded-xl border border-[#6f7d54]/15 px-3 py-2">
              <option value="camp">Camp</option>
              <option value="atelier">Atelier</option>
            </select>
          </label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Date</span><input type="date" value={newDate.date} onChange={(e) => setNewDate({ ...newDate, date: e.target.value })} data-testid="new-campdate-date" className="rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Places</span><input type="number" min="1" value={newDate.capacity} onChange={(e) => setNewDate({ ...newDate, capacity: parseInt(e.target.value) || 1 })} className="w-20 rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          {newDate.category === "camp" ? (
            <>
              <label className="text-sm flex items-center gap-2 pb-2"><input type="checkbox" checked={newDate.morning} onChange={(e) => setNewDate({ ...newDate, morning: e.target.checked })} /> Matin</label>
              <label className="text-sm flex items-center gap-2 pb-2"><input type="checkbox" checked={newDate.afternoon} onChange={(e) => setNewDate({ ...newDate, afternoon: e.target.checked })} /> Après-midi</label>
            </>
          ) : (
            <label className="text-sm"><span className="block font-semibold text-[#3a4d68] mb-1">Thème</span><input value={newDate.theme} onChange={(e) => setNewDate({ ...newDate, theme: e.target.value })} placeholder="ex. Poterie" className="rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
          )}
          <button onClick={add} data-testid="add-campdate" className="inline-flex items-center gap-1.5 rounded-full bg-[#e07a5f] text-white font-bold text-sm px-4 py-2.5"><Plus size={16} /> Ajouter</button>
        </div>
        {error && <p className="text-[#b5232f] text-sm font-semibold mt-2">{error}</p>}
      </Card>
      {loading ? <Loader2 className="animate-spin text-[#e07a5f]" /> : (
        <div className="space-y-8">
          <div>
            <p className="font-head font-bold text-[#6f7d54] mb-3">Camp ({camps.length})</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{camps.map(DateCard)}</div>
          </div>
          <div>
            <p className="font-head font-bold text-[#6f7d54] mb-3">Ateliers · Mercredis & Samedis ({ateliers.length})</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{ateliers.map(DateCard)}</div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- WORKSHOPS ---------------- */
const EMPTY_WS = { day_fr: "Mercredi", day_en: "Wednesday", title_fr: "", title_en: "", age: "", duration_fr: "", duration_en: "", desc_fr: "", desc_en: "", img: "", spots: 6, available: true };
function WorkshopsTab({ api }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_WS);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => { api.get("/admin/workshops").then((r) => { setItems(r.data.workshops); setLoading(false); }).catch(() => setLoading(false)); }, [api]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (editing) await api.put(`/admin/workshops/${editing}`, form);
    else await api.post("/admin/workshops", form);
    setForm(EMPTY_WS); setEditing(null); load();
  };
  const edit = (w) => { setEditing(w.id); setForm({ ...EMPTY_WS, ...w }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (id) => { if (!window.confirm("Supprimer cet atelier ?")) return; await api.delete(`/admin/workshops/${id}`); load(); };
  const F = (k, label, type = "text") => (
    <label className="text-sm block"><span className="block font-semibold text-[#3a4d68] mb-1">{label}</span><input type={type} value={form[k]} onChange={(e) => setForm({ ...form, [k]: type === "number" ? parseInt(e.target.value) || 0 : e.target.value })} className="w-full rounded-xl border border-[#6f7d54]/15 px-3 py-2" /></label>
  );

  return (
    <div data-testid="workshops-tab">
      <H2>Ateliers</H2>
      <Card className="p-4 mb-5">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {F("title_fr", "Titre (FR)")}{F("title_en", "Titre (EN)")}{F("age", "Âge")}
          {F("day_fr", "Jour (FR)")}{F("day_en", "Jour (EN)")}
          {F("duration_fr", "Durée (FR)")}{F("duration_en", "Durée (EN)")}
          {F("spots", "Places", "number")}{F("img", "Image (URL)")}
          {F("desc_fr", "Description (FR)")}{F("desc_en", "Description (EN)")}
          <label className="text-sm flex items-center gap-2 pt-6"><input type="checkbox" checked={form.available} onChange={(e) => setForm({ ...form, available: e.target.checked })} /> Disponible</label>
        </div>
        <div className="flex gap-2 mt-4">
          <button onClick={save} data-testid="save-workshop" className="inline-flex items-center gap-1.5 rounded-full bg-[#e07a5f] text-white font-bold text-sm px-5 py-2.5">{editing ? "Enregistrer" : <><Plus size={16} /> Ajouter</>}</button>
          {editing && <button onClick={() => { setEditing(null); setForm(EMPTY_WS); }} className="rounded-full bg-[#6f7d54]/8 text-[#3a4d68] font-bold text-sm px-5 py-2.5">Annuler</button>}
        </div>
      </Card>
      {loading ? <Loader2 className="animate-spin text-[#e07a5f]" /> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((w) => (
            <Card key={w.id} className="overflow-hidden">
              {w.img ? <img src={w.img} alt="" className="w-full h-32 object-cover" /> : <div className="w-full h-32 bg-[#e0b36a]/20" />}
              <div className="p-4">
                <div className="flex items-center justify-between">
                  <span className="font-head font-bold text-[#6f7d54]">{w.title_fr}</span>
                  {!w.available && <span className="text-xs font-bold text-[#b5232f]">Indispo</span>}
                </div>
                <div className="text-sm text-[#3a4d68]">{w.day_fr} · {w.age} · {w.duration_fr}</div>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => edit(w)} className="text-sm font-bold text-[#2e8b9e]">Éditer</button>
                  <button onClick={() => del(w.id)} data-testid={`del-workshop-${w.id}`} className="text-[#e63946]"><Trash2 size={15} /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- PARENTS ---------------- */
function ParentsTab({ api }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(() => { api.get("/admin/parents").then((r) => { setItems(r.data.parents); setLoading(false); }).catch(() => setLoading(false)); }, [api]);
  useEffect(() => { load(); }, [load]);
  const del = async (email) => {
    if (!window.confirm(`Supprimer ce parent et TOUTES ses réservations/adhésions (${email}) ? Action irréversible.`)) return;
    await api.delete(`/admin/parents/${encodeURIComponent(email)}`);
    load();
  };
  return (
    <div data-testid="parents-tab">
      <H2>Parents & enfants</H2>
      {loading ? <Loader2 className="animate-spin text-[#e07a5f]" /> : items.length === 0 ? <p className="font-body text-[#3a4d68]">Aucun parent enregistré.</p> : (
        <div className="grid sm:grid-cols-2 gap-3">
          {items.map((p, i) => (
            <Card key={i} className="p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2"><span className="font-head font-bold text-[#6f7d54]">{p.name}</span>{p.is_member && <span className="rounded-full bg-[#5e7d52]/15 text-[#4a6742] text-xs font-bold px-2 py-0.5">membre</span>}</div>
                <button onClick={() => del(p.email)} data-testid={`parent-delete-${p.email}`} title="Supprimer ce parent et ses données" className="text-[#e63946] shrink-0"><Trash2 size={16} /></button>
              </div>
              <div className="font-body text-sm text-[#3a4d68]">{p.email} · {p.phone || "—"}</div>
              {(p.emergency_name || p.emergency_phone) && <div className="text-sm text-[#b5232f] mt-1 flex items-center gap-1"><Phone size={13} /> {p.emergency_name} {p.emergency_phone}</div>}
              <div className="mt-3 space-y-1">
                {p.children.map((c, j) => (
                  <div key={j} className="text-sm text-[#3a4d68] flex items-center gap-2"><Baby size={14} className="text-[#e07a5f]" /> {c.name}{c.allergies ? <span className="text-[#cf6a50]">· allergies : {c.allergies}</span> : null}</div>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
